// Messages exchanged over the room WebSocket. Shared by client and server so
// both sides agree on the shape at compile time.

import {
  DEFAULT_HOUSE_RULES,
  MAX_MAP_SIZE,
  MAX_VICTORY_POINTS,
  MIN_MAP_SIZE,
  MIN_VICTORY_POINTS,
  type Action,
  type BoardLayout,
  type BotKind,
  type GameEvent,
  type GameView,
  type HouseRules,
} from '../engine';

export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ';
export const ROOM_CODE_LENGTH = 5;
export const MAX_NAME_LENGTH = 24;
/**
 * The engine has no player limit (the board grows with the table); this only
 * keeps one room from growing without bound.
 */
export const MAX_SEATS = 20;
/** Player colours to choose from (the client's PLAYER_PALETTE); one per seat, never shared. */
export const PALETTE_SIZE = 20;

/** The lowest palette colour nobody holds. */
export function firstFreeColor(taken: readonly number[]): number {
  let c = 0;
  while (taken.includes(c)) c++;
  return c;
}
export const MIN_PLAYERS_TO_START = 2;
/** Larger client messages are rejected unparsed. */
export const MAX_MESSAGE_BYTES = 8 * 1024;
/** How many past events a (re)joining client gets for its log. */
/**
 * Events replayed to a (re)joining socket. Large enough to be the whole game,
 * so the log can be scrolled back to the start, while keeping that one message
 * well under the WebSocket frame limit.
 */
export const HISTORY_EVENTS = 5000;
/** Text frame the server auto-answers with PONG, without waking a hibernated room. */
export const PING = 'ping';
export const PONG = 'pong';
/** Soft turn-timer choices in seconds; null means no timer. */
export const TURN_TIMER_CHOICES = [null, 10, 20, 30, 60, 120, 180] as const;
export type TurnSeconds = (typeof TURN_TIMER_CHOICES)[number];

export function isTurnSeconds(x: unknown): x is TurnSeconds {
  return (TURN_TIMER_CHOICES as readonly unknown[]).includes(x);
}

/** What the host chooses in the lobby. Everyone sees it live; it's used when the game starts. */
export interface GameSettings {
  layout: BoardLayout;
  turnSeconds: TurnSeconds;
  rules: HouseRules;
  /** Points needed to win. */
  victoryPoints: number;
  /** Middle-row width of the island, or 'auto' to size it by player count. */
  mapSize: number | 'auto';
  /** Who places first and takes the first turn: 'roll' for the dice to decide, or a seat. Absent from older clients: roll. */
  firstPlayer?: FirstPlayer;
}

export type FirstPlayer = 'roll' | number;

export const DEFAULT_SETTINGS: GameSettings = {
  layout: 'random',
  turnSeconds: null,
  rules: DEFAULT_HOUSE_RULES,
  victoryPoints: 10,
  mapSize: 'auto',
  firstPlayer: 'roll',
};

const isIntIn = (x: unknown, lo: number, hi: number): x is number => typeof x === 'number' && Number.isInteger(x) && x >= lo && x <= hi;

const RULE_KEYS = Object.keys(DEFAULT_HOUSE_RULES) as (keyof HouseRules)[];

/** Validates settings from the wire. Missing rules fall back to defaults; anything malformed is rejected. */
export function parseSettings(raw: unknown): GameSettings | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { layout, turnSeconds, rules, victoryPoints = 10, mapSize = 'auto', firstPlayer = 'roll' } = raw as Record<string, unknown>;
  if (layout !== 'random' && layout !== 'fixed') return null;
  if (!isTurnSeconds(turnSeconds)) return null;
  if (!isIntIn(victoryPoints, MIN_VICTORY_POINTS, MAX_VICTORY_POINTS)) return null;
  if (mapSize !== 'auto' && !isIntIn(mapSize, MIN_MAP_SIZE, MAX_MAP_SIZE)) return null;
  if (firstPlayer !== 'roll' && !isIntIn(firstPlayer, 0, MAX_SEATS - 1)) return null;
  if (rules !== undefined && (typeof rules !== 'object' || rules === null)) return null;
  const parsed: HouseRules = { ...DEFAULT_HOUSE_RULES };
  for (const k of RULE_KEYS) {
    const v = (rules as Record<string, unknown> | undefined)?.[k];
    if (v === undefined) continue;
    if (typeof v !== 'boolean') return null;
    parsed[k] = v;
  }
  return { layout, turnSeconds, rules: parsed, victoryPoints, mapSize, firstPlayer };
}

export function isRoomCode(code: string): boolean {
  if (code.length !== ROOM_CODE_LENGTH) return false;
  for (const ch of code) if (!ROOM_CODE_ALPHABET.includes(ch)) return false;
  return true;
}

export const MAX_CHAT_LENGTH = 300;
/** Chat lines a (re)joining client receives. */
export const CHAT_HISTORY = 100;
/** At most this many chat messages per seat in CHAT_WINDOW_MS. */
export const CHAT_BURST = 5;
export const CHAT_WINDOW_MS = 5000;

export interface ChatMessage {
  id: number;
  seat: number;
  /** Name at the time it was sent, so the line still reads right after seats change. */
  name: string;
  text: string;
  at: number;
}

/** Strips control characters (keeping none: chat is one line), trims, and caps length. Empty means invalid. */
export function cleanChat(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, MAX_CHAT_LENGTH);
}

/** Trims, strips control characters, and caps length. Empty means invalid. */
export function cleanName(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, MAX_NAME_LENGTH);
}

export type ClientMessage =
  /** First message on every connection; `token` reclaims a seat. */
  | { type: 'hello'; token?: string }
  /** Take a seat in the lobby. */
  | { type: 'join'; name: string }
  /** Give up your seat (lobby only). */
  | { type: 'leave' }
  /** Host only, after a win: keep playing to a higher target. */
  | { type: 'continue'; target: number }
  /** Host only, after a game ends: go back to the lobby (same seats) to change settings. */
  | { type: 'lobby' }
  /** Host only, before the game starts: change the settings everyone sees in the lobby. */
  | { type: 'configure'; settings: GameSettings }
  /**
   * Host only: start (or restart, after a game ends) with the seated players.
   * `settings` wins; the loose fields are what clients sent before settings existed.
   */
  | { type: 'start'; settings?: GameSettings; layout?: BoardLayout; turnSeconds?: TurnSeconds }
  | { type: 'action'; action: Action }
  /** Host only: take back the most recent action of the current game. */
  | { type: 'undo' }
  /** Host only, for an away seat: remove it (lobby) or open it for someone else to claim (in a game). */
  | { type: 'release'; seat: number }
  /** A visitor takes over an open seat. */
  | { type: 'claim'; seat: number }
  /** Seated players only. */
  | { type: 'chat'; text: string }
  /** Host only, in the lobby: add a computer player. (Remove it with `release`.) */
  | { type: 'addCpu'; kind?: BotKind }
  /** In the lobby: pick a colour for your own seat (or, as host, a CPU's). Colours can't be shared. */
  | { type: 'setColor'; color: number; seat?: number }
  /** Host only, in the lobby: change which strategy plays a CPU seat. */
  | { type: 'setCpu'; seat: number; kind: BotKind }
  /** Host only, mid-game: let the computer play an away player's seat. */
  | { type: 'cpuTakeover'; seat: number; kind?: BotKind }
  /** Seated players: send turn notifications to this device (a PushSubscription's JSON). */
  | { type: 'pushSubscribe'; subscription: unknown }
  | { type: 'pushUnsubscribe'; endpoint: string }
  /** Whether this page is on screen. Sent after every hello and on visibility changes; no push goes to a seat that's looking. */
  | { type: 'visible'; visible: boolean };

export type RoomStatus = 'lobby' | 'playing' | 'finished';

export interface SeatSummary {
  name: string;
  connected: boolean;
  /** Released by the host; any visitor can claim it. */
  open: boolean;
  /** Played by the computer. */
  cpu: boolean;
  /** Which strategy, for CPU seats. */
  bot?: BotKind;
  /** Index into the player palette. */
  color: number;
}

export interface RoomSnapshot {
  code: string;
  status: RoomStatus;
  seats: SeatSummary[];
  /** Seat 0 while connected, otherwise the lowest connected seat. */
  host: number;
  spectators: number;
  /** Soft turn timer for the current game, if any. */
  turnSeconds: TurnSeconds;
  /** The host's choices for the next game (and, once playing, the current one). */
  settings: GameSettings;
}

export type ServerMessage =
  /** `seat` is null for spectators and not-yet-joined visitors. `token` is sent once, on join. */
  | { type: 'welcome'; seat: number | null; token?: string }
  | { type: 'room'; room: RoomSnapshot }
  /**
   * The viewer's redacted state plus their legal actions. `events` are new
   * since the last message, or the recent history when `reset` is set.
   */
  | { type: 'game'; view: GameView; legal: Action[]; events: GameEvent[]; reset: boolean }
  /** New chat lines, or (with `reset`) the recent history for a (re)joining client. */
  | { type: 'chat'; messages: ChatMessage[]; reset: boolean }
  /** Something everyone should notice, like an undo. */
  | { type: 'info'; message: string }
  /** `fatal` errors are followed by the server closing the socket. */
  | { type: 'error'; message: string; fatal?: boolean };
